import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInvoices } from '../../hooks/useInvoices';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import { formatCurrency } from '../../lib/currency';
import type { Invoice } from '@motra/shared';

export default function InvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: invoices = [], isLoading } = useInvoices();

  // Client-side date filter
  const filtered = invoices.filter((inv: Invoice) => {
    if (!inv.created_at) return true;
    const d = inv.created_at.split('T')[0];
    return d >= fromDate && d <= toDate;
  });

  const columns = [
    { header: 'Invoice ID', accessor: 'id' as keyof Invoice },
    {
      header: t('date', 'Date'),
      accessor: 'created_at' as keyof Invoice,
      render: (val: unknown) =>
        val ? new Date(String(val)).toLocaleString() : '—',
    },
    {
      header: t('payment_type', 'Payment'),
      accessor: 'payment_type' as keyof Invoice,
      render: (val: unknown) => (
        <span className={`badge badge--${String(val)}`}>{String(val ?? '—')}</span>
      ),
    },
    {
      header: t('total', 'Total'),
      accessor: 'total' as keyof Invoice,
      render: (val: unknown) => formatCurrency(Number(val) || 0, currSym),
    },
    {
      header: t('status', 'Status'),
      accessor: 'status' as keyof Invoice,
      render: (val: unknown) => (
        <span
          className={`badge badge--${
            val === 'completed'
              ? 'success'
              : val === 'voided'
              ? 'danger'
              : 'neutral'
          }`}
        >
          {String(val ?? '—')}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id' as keyof Invoice,
      render: (_val: unknown, row: Invoice) => (
        <button
          className="btn btn--sm btn--outline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/invoices/${row.id}`);
          }}
        >
          {t('view', 'View')}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('invoices', 'Invoices')}</h1>
      </div>

      <div className="page-filters">
        <div className="form-group form-group--inline">
          <label className="form-label">From</label>
          <input
            type="date"
            className="form-input form-input--sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="form-group form-group--inline">
          <label className="form-label">To</label>
          <input
            type="date"
            className="form-input form-input--sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        rowKey={(row) => row.id}
        emptyMessage="No invoices found"
      />
    </div>
  );
}
